import { type ChangeEvent, type ReactNode, useCallback } from 'react';
import get from 'lodash/get';
import { CFormLabel, CFormInput, CFormFeedback } from '@coreui/react';
import type { IFieldComponentProps } from './types';

interface ITextInputProps extends IFieldComponentProps<string> {
  disabled?: boolean;
  type?: string;
  label?: ReactNode;
}

const TextInput = ({ field, form, disabled = false, type = 'text', label }: ITextInputProps) => {
  const { name } = field;
  const { touched, errors, setFieldValue, setFieldTouched } = form;

  const invalid = Boolean(get(touched, name) && get(errors, name));

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFieldValue(name, e.target.value);
    },
    [name, setFieldValue],
  );

  // formik 1's handleBlur took the call without an event (it marked an `undefined` path touched and validated);
  // formik 2's reads the event's target and throws without one. setFieldTouched marks the field and validates.
  const handleOnBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  return (
    <div className="mb-3">
      <CFormLabel htmlFor={name}>{label}</CFormLabel>
      <CFormInput
        {...field}
        id={name}
        disabled={disabled}
        type={type}
        invalid={invalid}
        onBlur={handleOnBlur}
        onChange={handleChange}
      />
      {invalid && (
        <CFormFeedback invalid className="help-block">
          {errors[name]}
        </CFormFeedback>
      )}
    </div>
  );
};

export default TextInput;
